/**
 * PasswordInput Component
 *
 * Password field with show/hide toggle and real-time strength indicator
 */

import { useState } from 'react';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { validatePassword } from '../../lib/supabase';

export function PasswordInput({
  value,
  onChange,
  placeholder = 'Enter password',
  showRequirements = false,
  label = 'Password',
  id = 'password',
  autoComplete = 'new-password',
  className = ''
}) {
  const [showPassword, setShowPassword] = useState(false);
  const { checks } = validatePassword(value || '');

  const requirements = [
    { key: 'minLength', label: 'At least 12 characters', met: checks.minLength },
    { key: 'hasUppercase', label: 'At least one uppercase letter (A-Z)', met: checks.hasUppercase },
    { key: 'hasLowercase', label: 'At least one lowercase letter (a-z)', met: checks.hasLowercase },
    { key: 'hasNumber', label: 'At least one number (0-9)', met: checks.hasNumber },
    { key: 'hasSpecial', label: 'At least one special character (!@#$%^&*)', met: checks.hasSpecial },
  ];

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="w-5 h-5" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
        </button>
      </div>

      {showRequirements && value && (
        <div className="mt-3 space-y-2">
          <p className="text-sm font-medium text-gray-600">Password requirements:</p>
          {requirements.map((req) => (
            <div
              key={req.key}
              className={`flex items-center gap-2 text-sm ${
                req.met ? 'text-green-600' : 'text-gray-500'
              }`}
            >
              {req.met ? (
                <Check className="w-4 h-4" />
              ) : (
                <X className="w-4 h-4" />
              )}
              <span>{req.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PasswordInput;
